import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NOTIFICATION_KIND_LABELS } from '@raro/domain';
import { Button, cx, Icon } from '@raro/ui';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../lib/api';
import { formatDate } from '../lib/format';
import type { Notification } from '../lib/types';

/** Sino: poucas notificações, cada uma pedindo uma ação. Lida ao clicar. */
export function NotificationsMenu() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const unread = useQuery({ queryKey: ['notifications', 'unread-count'], queryFn: () => api.get<{ unread: number }>('/notifications/unread-count'), refetchInterval: 60_000 });
  const list = useQuery({ queryKey: ['notifications', 'list'], queryFn: () => api.get<Notification[]>('/notifications'), enabled: open });
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['notifications'] });
  const read = useMutation({ mutationFn: (id: string) => api.post(`/notifications/${id}/read`), onSuccess: invalidate });
  const readAll = useMutation({ mutationFn: () => api.post('/notifications/read-all'), onSuccess: invalidate });

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const n = unread.data?.unread ?? 0;
  const go = (x: Notification) => {
    if (!x.readAt) read.mutate(x.id);
    setOpen(false);
    if (x.href && /^https?:\/\//.test(x.href)) window.location.assign(x.href);
    else if (x.href) navigate(x.href);
  };
  return (
    <div className="rr-bell" ref={ref}>
      <button type="button" className={cx('rr-bell__btn', n > 0 && 'rr-bell__btn--on')} onClick={() => setOpen(!open)} title="notificações" aria-label={`${n} notificações não lidas`}>
        <span className="rr-bell__icon" aria-hidden><Icon name="bell" size={18} /></span>
        {n > 0 && <span className="rr-bell__count">{n > 9 ? '9+' : n}</span>}
      </button>
      {open && (
        <div className="rr-bell__menu">
          <div className="rr-row rr-row--between" style={{ padding: 'var(--rr-space-3) var(--rr-space-4)' }}>
            <span className="rr-label">Notificações</span>
            {n > 0 && <Button size="sm" variant="quiet" onClick={() => readAll.mutate()}>marcar todas lidas</Button>}
          </div>
          <div className="rr-bell__list">
            {list.isSuccess && list.data.length === 0 && <p className="rr-faint" style={{ padding: 'var(--rr-space-4)', fontSize: 'var(--rr-text-sm)' }}>nada por aqui. aferições a cobrar, decisões do cliente e Soluções publicadas aparecem aqui.</p>}
            {list.data?.map((x) => (
              <button key={x.id} type="button" className={cx('rr-bell__item', !x.readAt && 'rr-bell__item--unread')} onClick={() => go(x)}>
                <span className="rr-bell__kind">{NOTIFICATION_KIND_LABELS[x.kind] ?? x.kind} · {formatDate(x.createdAt)}</span>
                <span className="rr-bell__title">{x.title}</span>
                {x.text && <span className="rr-bell__text">{x.text}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
